import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

export default function Navbar() {
  const { user, profile, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  async function handleSignOut() {
    setMenuOpen(false)
    await signOut()
    navigate('/login')
  }

  const isAdmin = ['staff', 'readonly_admin', 'admin', 'superadmin'].includes(profile?.role)
  const isActive = (path) => location.pathname === path

  const LangSwitcher = () => (
    <div className="flex items-center gap-1 text-sm font-medium">
      <button
        onClick={() => i18n.changeLanguage('en')}
        className={
          i18n.language === 'en'
            ? 'font-bold text-primary'
            : 'text-on-surface-variant hover:text-primary transition'
        }
      >
        EN
      </button>
      <span className="text-outline-variant">|</span>
      <button
        onClick={() => i18n.changeLanguage('es')}
        className={
          i18n.language === 'es'
            ? 'font-bold text-primary'
            : 'text-on-surface-variant hover:text-primary transition'
        }
      >
        ES
      </button>
    </div>
  )

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled
            ? 'bg-surface/95 backdrop-blur-md shadow-sm border-b border-outline-variant/50'
            : 'bg-surface/80 backdrop-blur-sm'
        }`}
      >
        <div className={`${location.pathname.startsWith('/admin') ? 'max-w-[1440px]' : 'max-w-6xl'} mx-auto px-4 sm:px-6 h-16 flex items-center justify-between`}>

          {/* Logo / brand */}
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 hover:opacity-90 transition"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0 shadow-sm">
              <span className="text-on-primary font-black text-base leading-none">O</span>
            </div>
            <span className="text-base font-bold text-on-surface tracking-tight">Oasis Joven</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden sm:flex items-center gap-5 text-sm font-medium">
            <LangSwitcher />

            <Link
              to="/help"
              className={`pb-0.5 transition ${
                isActive('/help')
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-on-surface-variant hover:text-primary'
              }`}
            >
              {t('nav.help')}
            </Link>

            {user ? (
              <>
                <Link
                  to="/"
                  className={`pb-0.5 transition ${
                    isActive('/')
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-on-surface-variant hover:text-primary'
                  }`}
                >
                  {t('nav.home')}
                </Link>
                <Link
                  data-testid="nav-my-registration"
                  to="/my-registration"
                  className={`pb-0.5 transition ${
                    isActive('/my-registration')
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-on-surface-variant hover:text-primary'
                  }`}
                >
                  {t('nav.myRegistration')}
                </Link>
                {isAdmin && (
                  <Link
                    data-testid="nav-admin-link"
                    to="/admin"
                    className={`pb-0.5 transition ${
                      isActive('/admin')
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-on-surface-variant hover:text-primary'
                    }`}
                  >
                    {t('nav.adminDashboard')}
                  </Link>
                )}
                <button
                  data-testid="nav-sign-out"
                  onClick={handleSignOut}
                  className="h-9 px-4 rounded-xl border border-outline text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition font-semibold"
                >
                  {t('nav.signOut')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="h-9 px-4 rounded-xl border border-outline text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition font-semibold inline-flex items-center"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="h-9 px-5 rounded-xl bg-primary text-on-primary hover:opacity-90 transition font-semibold inline-flex items-center shadow-sm"
                >
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>

          {/* Hamburger — mobile only */}
          <button
            className="sm:hidden flex flex-col justify-center items-center w-10 h-10 rounded-xl hover:bg-surface-container-low transition"
            onClick={() => setMenuOpen(prev => !prev)}
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <span className={`block w-5 h-0.5 bg-on-surface transition-all duration-200 ${menuOpen ? 'rotate-45 translate-y-[6px]' : ''}`} />
            <span className={`block w-5 h-0.5 bg-on-surface my-1 transition-opacity duration-200 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-on-surface transition-all duration-200 ${menuOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} />
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="sm:hidden border-t border-outline-variant bg-surface/97 backdrop-blur-md px-4 py-3 space-y-1 text-sm font-medium">
            <div className="px-3 py-2">
              <LangSwitcher />
            </div>
            <Link
              to="/help"
              onClick={() => setMenuOpen(false)}
              className="block rounded-xl px-3 py-3 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
            >
              {t('nav.help')}
            </Link>
            {user ? (
              <>
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-xl px-3 py-3 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
                >
                  {t('nav.home')}
                </Link>
                <Link
                  to="/my-registration"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-xl px-3 py-3 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
                >
                  {t('nav.myRegistration')}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="block rounded-xl px-3 py-3 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
                  >
                    {t('nav.adminDashboard')}
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full mt-1 rounded-xl border border-outline text-on-surface-variant px-3 py-3 font-semibold hover:bg-surface-container-low transition text-left"
                >
                  {t('nav.signOut')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-xl px-3 py-3 text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-xl bg-primary text-on-primary px-3 py-3 font-semibold hover:opacity-90 transition text-center"
                >
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Spacer for fixed navbar */}
      <div className="h-16" aria-hidden="true" />
    </>
  )
}
